import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { AnimatePresence, m } from 'framer-motion';
import config from '../../../../client-config.js';
import classNames from 'classnames';
import { Loader, Menu } from '@livecommerce/design-system';
import { Broadcaster, InputButton, AspectRatioButton, landscapeRotation, SourceSelections, SourceSelect, LoadingOverlay, WideButton, Controls, InputControls, isDraggingStyle, draggableStyle, draggablePortraitSize, draggableLandscapeSize, inlineStyle, Container, TopControls, isLive, LivePill } from './styles.js';
import { useHistory, useLocation } from 'react-router-dom';
import { useLocalization } from 'contexts/LocalizationContext.jsx';
import { useShow } from 'hooks/useShow.js';
import useCopyToClipboard from 'react-use/lib/useCopyToClipboard.js';
import { useAlert } from 'contexts/AlertContext.jsx';
import useDraggable from 'hooks/useDraggable.js';
import { useOrgId } from 'hooks/index.js';
import { useBroadcaster } from 'contexts/BroadcasterContext.jsx';
import NavigationDropdown from 'components-v3/NavigationDropdown/index.jsx';
import { buildRoute, ORG_SHOW, ORG_SHOW_MODERATE } from 'constants/routes.js';
import { PORTRAIT, LANDSCAPE, INITIAL_POSITION } from './constants.js';
import { embedCode, parseDevice } from './utils.js';

const BaasBroadcaster = ({
  broadcaster,
  broadcasterBroadcasting,
  broadcasterVideoSurface,
  broadcasterIsRegistered,
  broadcasterMuted,
  broadcasterCameraOn,
  broadcasterHasInputAccess,
  onClose,
  isInline,
  isLoading,
  eventId,
  publicBaseUrl,
  aspectRatio,
  onAspectRatioChange,
}) => {
  const { t } = useLocalization();
  const [_, copyToClipboard] = useCopyToClipboard();
  const { setAlert } = useAlert();
  const orgId = useOrgId();
  const { data: show = {}, isLoading: isLoadingShow } = useShow(eventId);
  const history = useHistory();
  const { pathname } = useLocation();
  const { switchVideoDevice, switchAudioDevice } = useBroadcaster();
  const [isMoreMenuOpen, setIsMoreMenuOpen] = useState(false);
  const [videoDevices, setVideoDevices] = useState([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState(null);
  const [audioDevices, setAudioDevices] = useState([]);
  const [selectedAudioDeviceId, setSelectedAudioDeviceId] = useState(null);

  useEffect(() => {
    if (!broadcaster || !broadcasterHasInputAccess) return;
    broadcaster.videoInputDevices.then((devices) => setVideoDevices(devices.map(parseDevice)));
    broadcaster.audioInputDevices.then((devices) => setAudioDevices(devices.map(parseDevice)));
    broadcaster.selectedVideoInput.then((device) => {
      if (device) setSelectedDeviceId(device.deviceId);
    });
    broadcaster.selectedAudioInput.then((device) => {
      if (device) setSelectedAudioDeviceId(device.deviceId);
    });
  }, [broadcaster, broadcasterHasInputAccess]);

  const selectedVideoItem = videoDevices.find((d) => d.value === selectedDeviceId);
  const selectedAudioItem = audioDevices.find((d) => d.value === selectedAudioDeviceId);

  const onAudioDeviceChange = useCallback((item) => {
    setSelectedAudioDeviceId(item.selectedItem.value);
    switchAudioDevice(item.selectedItem.value);
  }, [switchAudioDevice]);

  const onVideoDeviceChange = useCallback((item) => {
    setSelectedDeviceId(item.selectedItem.value);
    switchVideoDevice(item.selectedItem.value);
  }, [switchVideoDevice]);

  const moderateRoute = eventId ? buildRoute(ORG_SHOW_MODERATE, { orgId, showId: eventId }) : null;
  const showRoute = eventId ? buildRoute(ORG_SHOW, { orgId, showId: eventId, selectedTab: 'setup' }) : null;
  const showBasePath = eventId ? `/shows/${eventId}` : null;
  const previewUrl = eventId
    ? `${publicBaseUrl || config.publicBaseUrl}#type=overlay&autoplay=true&eventId=${encodeURIComponent(eventId)}`
    : null;

  const handleCopyEmbedCode = useCallback(() => {
    const embedScript = embedCode(eventId, config.embedBaseUrl);
    copyToClipboard(embedScript);
    setAlert({
      title: t('genericLabels.copiedAlertTitle'),
      variant: 'feedback',
    });
    setIsMoreMenuOpen(false);
  }, [eventId, copyToClipboard, setAlert, t]);

  const isOnModeratePage = !!moderateRoute && pathname === moderateRoute;
  const isOnShowPage = !!showBasePath && pathname.includes(showBasePath) && !isOnModeratePage;

  const moreMenuItems = useMemo(() => [
    ...(!isOnModeratePage ? [{
      label: t('shows.moderateMenuItem'),
      action: () => history.push(moderateRoute),
    }] : []),
    ...(!isOnShowPage ? [{
      label: t('genericLabels.goToShow'),
      action: () => history.push(showRoute),
    }] : []),
    ...(previewUrl ? [{
      label: t('genericLabels.preview'),
      href: previewUrl,
    }] : []),
    ...(eventId && config.embedBaseUrl ? [{
      label: t('embedScriptContainer.copyScript'),
      action: handleCopyEmbedCode,
    }] : []),
  ], [t, history, moderateRoute, showRoute, isOnModeratePage, isOnShowPage, previewUrl, eventId, handleCopyEmbedCode]);

  const { isDragging, handleMouseDown } = useDraggable({
    initialPosition: INITIAL_POSITION,
    onDrag: (position) => {
      const container = document.getElementById('baas-broadcaster-container');
      if (container) {
        container.style.right = `${position.right}px`;
        container.style.bottom = `${position.bottom}px`;
      }
    },
    getBounds: useCallback(() => {
      const container = document.getElementById('baas-broadcaster-container');
      const { innerWidth, innerHeight } = window;
      const { width, height } = container?.getBoundingClientRect() || { width: 0, height: 0 };
      return {
        right: innerWidth - width,
        bottom: innerHeight - height,
      };
    }, []),
    getCurrentPosition: useCallback(() => {
      const container = document.getElementById('baas-broadcaster-container');
      if (container) {
        return {
          right: parseFloat(container.style.right) || 20,
          bottom: parseFloat(container.style.bottom) || 20,
        };
      }
      return { right: 20, bottom: 20 };
    }, []),
  });

  const handleButtonMouseDown = useCallback((e) => {
    e.stopPropagation();
  }, []);

  const onAspectRatioToggle = useCallback(() => {
    onAspectRatioChange(aspectRatio === PORTRAIT ? LANDSCAPE : PORTRAIT);
  }, [aspectRatio, onAspectRatioChange]);

  const onShowMoreOptions = useCallback((e) => {
    e.stopPropagation();
    setIsMoreMenuOpen((prev) => !prev);
  }, []);

  const handleClick = useCallback(() => {
    if (!broadcaster || !broadcaster.stopBroadcasting || !broadcaster.start) return;
    if (broadcasterBroadcasting) {
      broadcaster.stopBroadcasting();
    } else {
      broadcaster.start();
    }
  }, [broadcaster, broadcasterBroadcasting]);

  const onToggleMute = useCallback(() => {
    if (!broadcaster) return;
    broadcaster.toggleMute();
  }, [broadcaster]);

  const onToggleCamera = useCallback(() => {
    if (!broadcaster) return;
    broadcaster.toggleCamera();
  }, [broadcaster, broadcasterBroadcasting]);

  const clickOutsideHandler = useCallback(() => setIsMoreMenuOpen(false), []);

  const buttonState = useMemo(() => {
    if (broadcasterBroadcasting) {
      return {
        variant: 'solidRed',
        label: t('moderateShow.stopButtonLabel'),
      };
    }
    return {
      variant: 'solidOrange',
      label: t(show?.isTestShow ? 'shows.startTest' : 'moderateShow.startButtonLabel'),
    };
  }, [broadcasterBroadcasting, show?.isTestShow, t]);

  const broadcasterClasses = classNames({
    [draggableStyle]: !isInline,
    [draggablePortraitSize]: !isInline && aspectRatio === PORTRAIT,
    [draggableLandscapeSize]: !isInline && aspectRatio === LANDSCAPE,
    [inlineStyle]: isInline,
    [isDraggingStyle]: isDragging,
  });

  const topControlsClasses = classNames({
    [isLive]: broadcasterBroadcasting && broadcasterIsRegistered,
  });

  const aspectRatioButtonClasses = classNames({
    [landscapeRotation]: aspectRatio === PORTRAIT,
  });

  return (
    <Broadcaster
      className={broadcasterClasses}
      id="baas-broadcaster-container"
      onMouseDown={isInline ? undefined : handleMouseDown}
    >
      <Container>
        {isLoading && <LoadingOverlay><Loader /></LoadingOverlay>}
        {broadcasterVideoSurface}
        <TopControls className={topControlsClasses}>
          <AnimatePresence>
            {broadcasterBroadcasting && broadcasterIsRegistered && (
              <m.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.25 }}
              >
                <LivePill variant="live">
                  {t('broadcast.liveTitle')}
                </LivePill>
              </m.div>
            )}
            {isInline && !broadcasterBroadcasting && (
              <SourceSelections>
                {videoDevices.length > 1 && (
                  <SourceSelect
                    id="camera-device-select"
                    items={videoDevices}
                    selectedItem={selectedVideoItem}
                    onSelectedItemChange={onVideoDeviceChange}
                    size="small"
                    theme="dark"
                    onMouseDown={handleButtonMouseDown}
                  />
                )}
                {audioDevices.length > 1 && (
                  <SourceSelect
                    id="microphone-device-select"
                    items={audioDevices}
                    selectedItem={selectedAudioItem}
                    onSelectedItemChange={onAudioDeviceChange}
                    size="small"
                    theme="dark"
                    onMouseDown={handleButtonMouseDown}
                  />
                )}
              </SourceSelections>
            )}
          </AnimatePresence>
          <InputControls>
            <NavigationDropdown
              isControlled
              isOpened={isMoreMenuOpen}
              onClickOutside={clickOutsideHandler}
              dropdownPosition="bottom"
              dropdownAlignment="end"
              dropdownContent={<Menu theme="dark" items={moreMenuItems} />}
              hoverBackgroundColor="transparent"
            >
              <InputButton
                onMouseDown={handleButtonMouseDown}
                variant="solid"
                iconName="more"
                onClick={onShowMoreOptions}
                aria-label={t('genericLabels.options')}
                title={t('genericLabels.options')}
              />
            </NavigationDropdown>
            {!isOnModeratePage && (
              <InputButton
                onMouseDown={handleButtonMouseDown}
                variant="solid"
                iconName="cancel"
                onClick={onClose}
                aria-label={t('genericLabels.close')}
                title={t('genericLabels.close')}
              />
            )}
          </InputControls>
        </TopControls>
        <Controls>
          <InputControls>
            <AspectRatioButton
              className={aspectRatioButtonClasses}
              variant="solid"
              iconName="portrait"
              onMouseDown={handleButtonMouseDown}
              onClick={onAspectRatioToggle}
              title={t(`uploadCallbackgroundComponent.${aspectRatio === PORTRAIT ? LANDSCAPE : PORTRAIT}Title`)}
              aria-label={t(`uploadCallbackgroundComponent.${aspectRatio === PORTRAIT ? LANDSCAPE : PORTRAIT}Title`)}
              disabled={broadcasterBroadcasting}
            />
            <InputButton
              onMouseDown={handleButtonMouseDown}
              variant="solid"
              iconName={broadcasterMuted === false ? 'mic' : 'micOff'}
              aria-label={t('genericLabels.microphone')}
              title={t('genericLabels.microphone')}
              aria-pressed={!broadcasterMuted}
              onClick={onToggleMute}
              disabled={!broadcaster || !broadcasterHasInputAccess || isLoading}
              loading={!broadcaster}
            />
            <InputButton
              onMouseDown={handleButtonMouseDown}
              variant="solid"
              iconName={broadcasterCameraOn ? 'videos' : 'videosOff'}
              aria-label={t('genericLabels.camera')}
              title={t('genericLabels.camera')}
              aria-pressed={!!broadcasterCameraOn}
              onClick={onToggleCamera}
              disabled={!broadcaster || !broadcasterHasInputAccess || isLoading}
              loading={!broadcaster}
            />
          </InputControls>
          <WideButton
            disabled={!broadcaster || (!broadcasterIsRegistered && broadcasterBroadcasting) || !broadcasterHasInputAccess || isLoading || isLoadingShow}
            loading={!broadcaster || (!broadcasterIsRegistered && broadcasterBroadcasting) || isLoadingShow}
            onMouseDown={handleButtonMouseDown}
            onClick={handleClick}
            variant={buttonState.variant}
            aria-pressed={broadcasterBroadcasting}
          >
            {buttonState.label}
          </WideButton>
        </Controls>
      </Container>
    </Broadcaster>
  );
};

export default memo(BaasBroadcaster);
